import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { api, errorMessage } from '../lib/api';
import type { PublicUser } from '../lib/types';
import { PageHeader } from '../components/PageHeader';
import { Person } from '../components/Avatar';
import { RowMenu } from '../components/RowMenu';
import { Field } from '../components/Field';
import { Banner } from '../components/Banner';
import { BulkEnrol } from '../components/BulkEnrol';
import { useConfirm } from '../components/Confirm';
import { GroupBar, type GroupSummary } from '../components/GroupBar';
import { formatPhone, isPhone, normalisePhone } from '../lib/phone';
import { arrangeStudents, type StudentSortKey } from '../lib/student-list';
import { nextSort, type SortState } from '../lib/sorting';
import { SortHeader } from '../components/SortHeader';
import { RosterField, type RosterChoice } from '../components/RosterField';
import { useAuth } from '../store/auth';
import { useWorkspace } from '../store/workspace';
import { SlipButton, slipFor, useSlipCentre } from '../components/SlipButton';
import { downloadSlips, type Slip } from '../lib/slips';

interface ResetOutcome {
  member: PublicUser;
  password: string;
  sessionsEnded: number;
  sittingInProgress: boolean;
}

/** The select's value for "no teacher": not an id, and not the empty "unchosen". */
const NO_TEACHER = 'none';

export function StudentsPage() {
  const me = useAuth((s) => s.user);
  const organizationId = useWorkspace((w) => w.organizationId);
  const [students, setStudents] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({ fullName: '', phone: '', password: '', groupName: '' });
  /**
   * Which teacher the next student goes to — the administrator's choice; null
   * is no teacher yet. A teacher's own enrolments are theirs whatever this
   * says, and the field is not shown to them.
   */
  const [enrolTo, setEnrolTo] = useState<RosterChoice>(null);
  /** The teacher the students on screen are being given to: an id, NO_TEACHER, or unchosen. */
  const [giveTo, setGiveTo] = useState('');
  const [giving, setGiving] = useState(false);
  /** Teachers, for naming the one a student belongs to. Empty for a teacher. */
  const [teachers, setTeachers] = useState<PublicUser[]>([]);
  const [groups, setGroups] = useState<GroupSummary[]>([]);
  const [ungrouped, setUngrouped] = useState(0);
  /** undefined: everybody. null: the students in no class. A name: that class. */
  const [filter, setFilter] = useState<string | null | undefined>(undefined);
  const [moving, setMoving] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState<StudentSortKey>>({ key: 'name', direction: 'asc' });
  const [reset, setReset] = useState<ResetOutcome | null>(null);
  /** The slip for the student just enrolled: their password exists readably only now. */
  const [lastSlip, setLastSlip] = useState<Slip | null>(null);
  const [printing, setPrinting] = useState(false);
  const slipCentre = useSlipCentre();
  const [resetting, setResetting] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { ask, dialog } = useConfirm();

  async function load() {
    setLoading(true);
    try {
      // The register and the classes together: the counts on the chips have to
      // agree with the rows underneath them, so they are read in one go.
      const [register, classes] = await Promise.all([
        api.get<{ members: PublicUser[] }>('/users', {
          params: {
            role: 'STUDENT',
            ...(filter === undefined ? {} : { group: filter === null ? 'none' : filter }),
          },
        }),
        api.get<{ groups: GroupSummary[]; ungrouped: number }>('/users/groups'),
      ]);
      setStudents(register.data.members);
      setGroups(classes.data.groups);
      setUngrouped(classes.data.ungrouped);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load students'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    // Only an administrator sees a column of teachers' names, so only they
    // need the list. A teacher's register is theirs and the centre's.
    if (me?.role !== 'SUPER_ADMIN' || !organizationId) return;
    api
      .get<{ members: PublicUser[] }>('/users', {
        params: { role: 'INSTRUCTOR', organizationId },
      })
      .then(({ data }) => setTeachers(data.members))
      .catch(() => setTeachers([]));
  }, [me?.role, organizationId]);

  const teacherName = useMemo(() => {
    const byId = new Map(teachers.map((t) => [t.id, t.fullName]));
    return (id: string | null) => (id === null ? 'No teacher yet' : byId.get(id) ?? 'A teacher');
  }, [teachers]);

  const shown = useMemo(
    () => arrangeStudents(students, { query, key: sort.key, direction: sort.direction }),
    [students, query, sort],
  );

  /**
   * Giving the students on screen to a teacher — the administrator's tool.
   *
   * It acts on what the register is showing, so a class is given by choosing
   * the class, and a handful by searching for them. It is how students who
   * were once shared by the whole centre get a teacher again.
   */
  async function giveShown() {
    if (!giveTo || shown.length === 0) return;
    const instructorId = giveTo === NO_TEACHER ? null : giveTo;
    const who = instructorId === null ? null : teacherName(instructorId);
    const count = `${shown.length} student${shown.length === 1 ? '' : 's'}`;
    const ok = await ask({
      title: who ? `Give ${count} to ${who}?` : `Take ${count} away from their teachers?`,
      body: (
        <p>
          {who
            ? `They will be on ${who}’s register and on no other teacher’s. Everything they have sat goes with them.`
            : 'They will have no teacher, and only you will see them until you give them to one.'}
        </p>
      ),
      confirmLabel: who ? `Give to ${who}` : 'Take them away',
    });
    if (!ok) return;
    setGiving(true);
    setError(null);
    try {
      const { data } = await api.post<{ moved: number; teacher: string | null }>('/users/teacher', {
        studentIds: shown.map((student) => student.id),
        instructorId,
        organizationId,
      });
      setNotice(
        data.teacher
          ? `${data.moved} student${data.moved === 1 ? '' : 's'} given to ${data.teacher}.`
          : `${data.moved} student${data.moved === 1 ? '' : 's'} now with no teacher.`,
      );
      setGiveTo('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not give those students to that teacher'));
    } finally {
      setGiving(false);
    }
  }

  /**
   * Sign-in slips for the students on screen who have not signed in yet.
   *
   * A password is stored only as a hash, so printing a slip means making a new
   * password. That is done only for students still holding a temporary one;
   * anyone who has chosen their own is left out, because replacing it would
   * lock them out of an account they are using.
   */
  async function printSlips() {
    const waiting = shown.filter((s) => s.mustChangePassword);
    const own = shown.length - waiting.length;
    if (waiting.length === 0) {
      setNotice(
        shown.length === 0
          ? 'There is nobody on this list to print a slip for.'
          : 'Everyone on this list has already chosen their own password, so there are no slips to print.',
      );
      return;
    }
    const ok = await ask({
      title: `Print sign-in slips for ${waiting.length} ${waiting.length === 1 ? 'student' : 'students'}?`,
      body: (
        <>
          <p>
            Each gets a new temporary password, printed on their slip — ten slips to an A4 page. A
            slip printed for them before stops working.
          </p>
          {own > 0 && (
            <p>
              {own} {own === 1 ? 'student has' : 'students have'} already chosen their own
              password and {own === 1 ? 'is' : 'are'} left out.
            </p>
          )}
        </>
      ),
      confirmLabel: `Print ${waiting.length}`,
    });
    if (!ok) return;
    setPrinting(true);
    setError(null);
    try {
      const { data } = await api.post<{
        issued: { fullName: string; candidateNumber: string | null; phone: string | null; password: string }[];
        keptOwn: number;
        skipped: number;
      }>('/users/reissue-passwords', { ids: waiting.map((s) => s.id) });
      await downloadSlips({ ...slipCentre, slips: data.issued.map(slipFor) }, 'sign-in-slips.pdf');
      setNotice(
        `Sign-in slips for ${data.issued.length} ${data.issued.length === 1 ? 'student' : 'students'} downloaded — print them and hand them out.` +
          (own > 0 ? ` ${own} who already chose their own password ${own === 1 ? 'was' : 'were'} left out.` : ''),
      );
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not make the slips'));
    } finally {
      setPrinting(false);
    }
  }

  function sortBy(key: StudentSortKey) {
    // A register is read by name; a number column is read smallest first,
    // which is the order the candidate numbers were handed out in.
    setSort((current) => nextSort(current, key, 'asc'));
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post<{ member: PublicUser; password?: string }>('/users', {
        ...form,
        // Empty means "use the candidate number", which the server hands out.
        phone: form.phone.trim() || null,
        // Empty means "make one", which the server hands back once.
        password: form.password.trim() || undefined,
        groupName: form.groupName.trim() || null,
        role: 'STUDENT',
        instructorId: enrolTo,
      });
      // The candidate number is allocated server-side, so it is worth showing:
      // it is what the student will see in the exam header.
      setNotice(
        `${data.member.fullName} enrolled as candidate ${data.member.candidateNumber ?? '—'}` +
          `${data.member.groupName ? `, in ${data.member.groupName}` : ''}` +
          `${me?.role === 'SUPER_ADMIN' && data.member.instructorId === null ? ', with no teacher yet' : ''}` +
          ` — signs in with ${formatPhone(data.member.phone)}.`,
      );
      setLastSlip(slipFor({ ...data.member, password: data.password ?? form.password.trim() }));
      // The class is kept: enrolling a walk-in usually means enrolling several
      // into the same one, and retyping it every time is a chore.
      setForm((f) => ({ fullName: '', phone: '', password: '', groupName: f.groupName }));
      setAdding(false);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not enrol that student'));
    } finally {
      setBusy(false);
    }
  }

  /**
   * The recovery path for a forgotten password. There is no email, so the
   * teacher does it here and reads the new password out — which is why it is
   * shown once, large, and never again.
   */
  async function resetPassword(student: PublicUser) {
    setResetting(student.id);
    setError(null);
    setNotice(null);
    setCopied(false);
    try {
      const { data } = await api.post<ResetOutcome>(`/users/${student.id}/reset-password`);
      setReset(data);
    } catch (err) {
      setError(errorMessage(err, 'Could not reset that password'));
    } finally {
      setResetting(null);
    }
  }

  /**
   * Moving one student, by typing over their class.
   *
   * An ordinary text box rather than a menu, because the first student in a
   * class has to be able to name it — a menu can only offer the classes that
   * already exist, and the way one comes into existence is somebody typing it.
   * Existing names complete as you type, so the second student is one keypress.
   */
  async function moveTo(student: PublicUser, typed: string) {
    const next = typed.trim() || null;
    if (next === (student.groupName ?? null)) return;

    setMoving(student.id);
    setError(null);
    setNotice(null);
    try {
      await api.patch(`/users/${student.id}`, { groupName: next });
      setNotice(
        next
          ? `${student.fullName} moved to ${next}.`
          : `${student.fullName} is no longer in a class.`,
      );
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not move that student'));
      await load();
    } finally {
      setMoving(null);
    }
  }

  async function remove(student: PublicUser) {
    const yes = await ask({
      title: `Remove ${student.fullName}?`,
      body: (
        <>
          <p>They can no longer sign in, and the number is free to enrol again.</p>
          <p>Everything they have already sat is kept, and stays in the results.</p>
        </>
      ),
      confirmLabel: 'Remove student',
      tone: 'danger',
    });
    if (!yes) return;

    setRemoving(student.id);
    setError(null);
    setNotice(null);
    try {
      await api.delete(`/users/${student.id}`);
      setNotice(`${student.fullName} removed.`);
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not remove that student'));
    } finally {
      setRemoving(null);
    }
  }

  return (
    <>
      {dialog}
      {/* Every class box on the page completes from the same list. */}
      <datalist id="student-classes">
        {groups.map((group) => (
          <option key={group.name} value={group.name} />
        ))}
      </datalist>
      <PageHeader
        title="Students"
        meta={
          <p className="muted">
            <span className="mono">{students.length}</span> enrolled
          </p>
        }
        actions={
          <>
            <button className="btn" disabled={printing} onClick={() => void printSlips()}>
              {printing ? 'Preparing slips…' : 'Print sign-in slips'}
            </button>
            <button
              className="btn"
              onClick={() => {
                setBulk((v) => !v);
                setAdding(false);
              }}
            >
              {bulk ? 'Cancel' : 'Enrol a class'}
            </button>
            <button
              className="btn btn--primary"
              onClick={() => {
                setAdding((v) => !v);
                setBulk(false);
              }}
            >
              {adding ? 'Cancel' : 'Enrol one student'}
            </button>
          </>
        }
      />

      <div className="stack">
        {error && <Banner tone="error">{error}</Banner>}
        {notice && <Banner tone="success">{notice}</Banner>}
        {lastSlip && (
          <div className="row">
            <SlipButton
              primary
              slips={[lastSlip]}
              filename={`sign-in-slip-${lastSlip.candidateNumber ?? 'student'}.pdf`}
              label={`Download ${lastSlip.name}'s sign-in slip (PDF)`}
            />
            <button className="btn btn--ghost" onClick={() => setLastSlip(null)}>
              Done
            </button>
          </div>
        )}

        {reset && (
          <section className="panel stack rise">
            <div>
              <p className="eyebrow">New password for {reset.member.fullName}</p>
              <h2 className="panel__title" style={{ marginBottom: 'var(--s-2)' }}>
                Read this out now — it is not shown again
              </h2>
              <p className="muted" style={{ maxWidth: '58ch' }}>
                It is stored only as a hash, so nobody, including you, can look it up later. If it
                is lost, reset it again.
              </p>
            </div>

            <div className="row" style={{ gap: 'var(--s-4)' }}>
              <code className="newpassword mono">{reset.password}</code>
              <button
                className="btn"
                onClick={() => {
                  void navigator.clipboard?.writeText(reset.password).then(() => setCopied(true));
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>

            {reset.sittingInProgress && (
              <Banner tone="error">
                {reset.member.fullName.split(' ')[0]} is part-way through a paper. The reset has
                signed them out — the clock keeps running, so give them the new password now. Their
                answers are saved and the paper resumes where they left off.
              </Banner>
            )}

            {reset.sessionsEnded > 0 && !reset.sittingInProgress && (
              <p className="field__hint">
                Signed out of{' '}
                <span className="mono">{reset.sessionsEnded}</span>{' '}
                {reset.sessionsEnded === 1 ? 'device' : 'devices'}, so the old password no longer
                opens anything.
              </p>
            )}

            <div className="row">
              <SlipButton
                slips={[slipFor({ ...reset.member, password: reset.password })]}
                filename={`sign-in-slip-${reset.member.candidateNumber ?? 'student'}.pdf`}
                label="Download sign-in slip (PDF)"
              />
              <button className="btn" onClick={() => setReset(null)}>
                Done
              </button>
            </div>
          </section>
        )}

        <GroupBar
          groups={groups}
          ungrouped={ungrouped}
          selected={filter}
          onSelect={setFilter}
          onChanged={(message) => {
            setNotice(message);
            void load();
          }}
        />

        {bulk && (
          <BulkEnrol
            groups={groups}
            organizationId={organizationId}
            onDone={() => void load()}
          />
        )}

        {adding && (
          <form className="panel stack rise" onSubmit={(e) => void add(e)}>
            <h2 className="panel__title">Enrol a student</h2>
            <div className="form-grid">
              {/*
                * A name or a code. With a code, Target never holds who the
                * student is — the centre keeps that list itself.
                */}
              <Field
                label="Name or code"
                required
                placeholder="Aziza Karimova, or S-017"
                value={form.fullName}
                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                hint="Shown in the exam header. Use a code and Target never holds the student's name — keep your own list of who is who."
              />
              {/*
                * Optional. Left empty, the student signs in with their
                * candidate number, so nobody's phone number reaches Target.
                */}
              <Field
                label="Sign-in number"
                type="tel"
                inputMode="numeric"
                placeholder="Leave empty to use the candidate number"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                error={form.phone && !isPhone(form.phone) ? 'Use 6 to 15 digits.' : null}
                hint={
                  form.phone.trim()
                    ? isPhone(form.phone)
                      ? `They sign in with ${formatPhone(normalisePhone(form.phone))}. Any number works — it need not be a real phone; Target never calls or texts it.`
                      : 'Any number of 6 to 15 digits. It need not be a real phone.'
                    : 'Optional. Left empty, they sign in with the candidate number they are given.'
                }
              />
              <Field
                label="Class"
                list="student-classes"
                value={form.groupName}
                placeholder="Monday evening"
                onChange={(e) => setForm((f) => ({ ...f, groupName: e.target.value }))}
                hint="Optional. Type a new name to start a class, or pick one you already teach."
              />
              <RosterField
                value={enrolTo}
                onChange={setEnrolTo}
                organizationId={organizationId}
              />
              <Field
                label="Temporary password"
                type="text"
                minLength={8}
                placeholder="Leave empty to make one"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                hint="Optional. Left empty, one is made — letters and numbers, printed on their sign-in slip."
              />
            </div>
            <div className="row">
              <button className="btn btn--primary" type="submit" disabled={busy}>
                {busy ? 'Enrolling…' : 'Enrol student'}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <p className="muted">Loading…</p>
        ) : students.length === 0 ? (
          <div className="empty">
            <p>{filter === undefined ? 'No students yet.' : 'Nobody in this class yet.'}</p>
            <p className="muted">Enrol one to start assigning practice tests.</p>
          </div>
        ) : (
          <>
            {/* Always, however short the register is: a box nobody can see is
                a feature nobody has. */}
            {(
              <div className="listbar">
                <input
                  type="search"
                  className="field__input listbar__search"
                  placeholder="Find by name, number or candidate"
                  aria-label="Find a student by name, sign-in number or candidate number"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query.trim() && (
                  <p className="muted">
                    <span className="mono">{shown.length}</span> of{' '}
                    <span className="mono">{students.length}</span>
                  </p>
                )}
                {me?.role === 'SUPER_ADMIN' && shown.length > 0 && (
                  <div className="giveto">
                    <label className="giveto__label" htmlFor="give-to">
                      Give {shown.length === 1 ? 'this student' : `these ${shown.length}`} to
                    </label>
                    <select
                      id="give-to"
                      className="field__input giveto__select"
                      value={giveTo}
                      onChange={(e) => setGiveTo(e.target.value)}
                    >
                      <option value="">Choose a teacher…</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.fullName}
                        </option>
                      ))}
                      <option value={NO_TEACHER}>No teacher — only you</option>
                    </select>
                    <button
                      type="button"
                      className="btn"
                      disabled={!giveTo || giving}
                      onClick={() => void giveShown()}
                    >
                      {giving ? 'Giving…' : 'Give'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {shown.length === 0 ? (
              <div className="empty">
                <p>Nobody matches “{query.trim()}”.</p>
                <p className="muted">
                  A name or code, a candidate number, or a sign-in number.
                </p>
                <button className="btn" onClick={() => setQuery('')}>
                  Show all {students.length}
                </button>
              </div>
            ) : (
          <div className="ledger__wrap">
            <table className="ledger">
              <thead>
                <tr>
                  <SortHeader label="Candidate" column="candidate" sort={sort} onSort={sortBy} numeric />
                  <SortHeader label="Name" column="name" sort={sort} onSort={sortBy} />
                  <SortHeader label="Sign-in" column="phone" sort={sort} onSort={sortBy} />
                  <SortHeader label="Class" column="group" sort={sort} onSort={sortBy} />
                  {/* Whose student, for the one account that can see both
                      teachers' registers at once. A teacher reading their own
                      register already knows the answer for every row on it. */}
                  {me?.role === 'SUPER_ADMIN' && <th>Enrolled to</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((student) => (
                  <tr key={student.id}>
                    <td className="num">{student.candidateNumber ?? '—'}</td>
                    <td>
                      <Person name={student.fullName} />
                    </td>
                    <td className="mono muted">{formatPhone(student.phone)}</td>
                    <td>
                      <input
                        className="groupcell"
                        list="student-classes"
                        defaultValue={student.groupName ?? ''}
                        placeholder="No class"
                        maxLength={120}
                        disabled={moving === student.id}
                        aria-label={`Class for ${student.fullName}`}
                        onBlur={(e) => void moveTo(student, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.currentTarget.blur();
                          if (e.key === 'Escape') {
                            e.currentTarget.value = student.groupName ?? '';
                            e.currentTarget.blur();
                          }
                        }}
                      />
                    </td>
                    {me?.role === 'SUPER_ADMIN' && (
                      <td className={student.instructorId === null ? 'muted' : undefined}>
                        {teacherName(student.instructorId)}
                      </td>
                    )}
                    <td style={{ textAlign: 'right' }}>
                      {/* The everyday action on show; removing a student,
                          which cannot be taken back, one deliberate click
                          further away behind the menu. */}
                      <div className="row" style={{ justifyContent: 'flex-end', gap: 'var(--s-1)' }}>
                        <button
                          className="btn btn--sm"
                          disabled={resetting === student.id}
                          onClick={() => void resetPassword(student)}
                        >
                          {resetting === student.id ? 'Resetting…' : 'Reset password'}
                        </button>
                        <RowMenu
                          label={`More for ${student.fullName}`}
                          items={[
                            {
                              label: removing === student.id ? 'Removing…' : 'Remove student',
                              hint: 'Stops them signing in; keeps their results',
                              icon: 'trash',
                              danger: true,
                              disabled: removing === student.id,
                              onSelect: () => void remove(student),
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
